import { useEffect, useState } from "react";
import { PenNib, Plus, Truck } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSiteImages } from "../context/SiteImagesContext";
import { toast } from "sonner";

export default function Boligrafos() {
  const { addItem } = useCart();
  const { resolve } = useSiteImages();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  if (!pricing?.pens) return null;

  const { colors, unit_price: unit, pair_price: pair } = pricing.pens;
  const shipping = pricing?.shipping?.price_es;

  const add = (color, qty) => {
    const price = qty === 2 ? pair : unit;
    addItem({
      key: `pen__${color.id}__${qty}`,
      kind: "pen",
      pen_color: color.id,
      pen_qty: qty,
      model_id: null,
      model_name: "Bolígrafo VITAE",
      size: null,
      size_label: `${color.label} · ${qty === 2 ? "2 unidades (oferta)" : "1 unidad"}`,
      finish: null,
      finish_label: "",
      specialties: [],
      name_personalization: null,
      price,
      image: resolve(`pen:${color.id}`, "") || null,
      pen_hex: color.hex,
      slug: `pen-${color.id}-${qty}`,
    });
    toast.success(`Bolígrafo ${color.label.toLowerCase()} añadido al carrito`);
  };

  return (
    <section id="boligrafos" data-testid="pens-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <p className="overline mb-6">
            <span className="text-[#D7C6A3]">07</span> &nbsp;/&nbsp; Bolígrafos VITAE
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D3732] leading-tight">
            El detalle que acompaña a tu carpeta.
          </h2>
          <p className="text-[#6B705C] mt-5 font-light leading-relaxed">
            Bolígrafos VITAE en tonos pastel, suaves y elegantes, pensados para vivir dentro de
            tu carpeta. <strong className="font-normal text-[#2D3732]">{unit.toFixed(2)} € la unidad</strong>{" "}
            o <strong className="font-normal text-[#2D3732]">{pair.toFixed(2)} € el pack de 2</strong>.
            {shipping ? ` Envío ${shipping.toFixed(2)} €.` : ""}
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-6 mt-12 max-w-3xl">
          {colors.map((color, i) => {
            const img = resolve(`pen:${color.id}`, "");
            return (
              <Reveal key={color.id} delay={0.08 * i}>
                <div
                  data-testid={`pen-card-${color.id}`}
                  className="bg-[#FAF9F6] border border-[#EBEBEB] rounded-3xl overflow-hidden"
                >
                  <div
                    className="h-44 flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${color.hex}55, ${color.hex}22)` }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={`Bolígrafo VITAE ${color.label}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <PenNib size={56} weight="thin" style={{ color: color.hex }} />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2.5">
                      <span
                        className="w-3.5 h-3.5 rounded-full border border-black/10"
                        style={{ background: color.hex }}
                      />
                      <h3 className="font-display text-xl text-[#2D3732]">
                        Bolígrafo VITAE · {color.label}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5 mt-5">
                      <button
                        data-testid={`pen-add-${color.id}-1`}
                        onClick={() => add(color, 1)}
                        className="border border-[#A3B18A] text-[#2D3732] hover:bg-[#EEF1E8] rounded-full py-3 text-sm transition-colors duration-300 flex items-center justify-center gap-1.5"
                      >
                        <Plus size={14} /> 1 ud · {unit.toFixed(2)} €
                      </button>
                      <button
                        data-testid={`pen-add-${color.id}-2`}
                        onClick={() => add(color, 2)}
                        className="bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full py-3 text-sm transition-colors duration-300 flex items-center justify-center gap-1.5"
                      >
                        <Plus size={14} /> 2 uds · {pair.toFixed(2)} €
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <p className="text-xs text-[#9CA3AF] mt-6 flex items-center gap-2">
            <Truck size={15} weight="light" />
            Envío estándar o InPost, el mismo que para las carpetas.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
